/**
 * Die geteilte Flaeche: links der Beleg, rechts ein Feld, dazwischen eine
 * Trennlinie, die sich ziehen laesst und ihre Stelle merkt. Gemeinsam fuer
 * die Dokumentseite und die Ansicht einer Rueckmeldung.
 *
 * Nebeneinander und nicht hintereinander, weil beim Lesen einer Analyse
 * staendig die Frage kommt "wo steht das". Wer dafuer die Ansicht wechseln
 * muss, prueft nicht nach.
 *
 * `daneben` steht rechts neben dem Feld in fester Breite, etwa die Spalte mit
 * den Rueckmeldungen.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Blattbetrachter } from './Blattbetrachter'

/* Wie breit das Analysefeld steht, in Prozent der Flaeche. Bleibt ueber
   Sitzungen erhalten: wer sich die Aufteilung einmal eingerichtet hat, will
   sie beim naechsten Dokument wiederfinden. */
const SPEICHER_SCHLUESSEL = 'jabwa.analysebreite'
const BREITE_STANDARD = 42
const BREITE_MIN = 24
const BREITE_MAX = 68

function breiteLaden(): number {
  try {
    const roh = Number(localStorage.getItem(SPEICHER_SCHLUESSEL))
    if (Number.isFinite(roh) && roh >= BREITE_MIN && roh <= BREITE_MAX) return roh
  } catch {
    // Privates Fenster oder gesperrter Speicher. Dann eben der Standardwert.
  }
  return BREITE_STANDARD
}

export function Geteilt({ quelle, titel, children, daneben }: { quelle: string; titel: string; children: ReactNode; daneben?: ReactNode }) {
  /* ── Die Trennlinie ──────────────────────────────────────────────────
     Gerechnet wird in Prozent der Flaeche, nicht in Pixeln: sonst steht das
     Analysefeld auf einem grossen Bildschirm als schmaler Streifen da. */
  const flaeche = useRef<HTMLDivElement>(null)
  const [breite, setBreite] = useState(breiteLaden)
  const [zieht, setZieht] = useState(false)

  const setzeBreite = useCallback((wert: number) => {
    const begrenzt = Math.min(BREITE_MAX, Math.max(BREITE_MIN, wert))
    setBreite(begrenzt)
    try {
      localStorage.setItem(SPEICHER_SCHLUESSEL, String(Math.round(begrenzt)))
    } catch {
      // Nicht schlimm, dann gilt die Breite nur fuer diese Sitzung.
    }
  }, [])

  const griffGedrueckt = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    setZieht(true)
  }, [])

  useEffect(() => {
    if (!zieht) return
    const bewegen = (e: PointerEvent) => {
      const kasten = flaeche.current?.getBoundingClientRect()
      if (!kasten || kasten.width === 0) return
      setzeBreite(((kasten.right - e.clientX) / kasten.width) * 100)
    }
    const loslassen = () => setZieht(false)
    window.addEventListener('pointermove', bewegen)
    window.addEventListener('pointerup', loslassen)
    window.addEventListener('pointercancel', loslassen)
    return () => {
      window.removeEventListener('pointermove', bewegen)
      window.removeEventListener('pointerup', loslassen)
      window.removeEventListener('pointercancel', loslassen)
    }
  }, [zieht, setzeBreite])

  /* Ohne Maus: die Trennlinie ist fokussierbar und laesst sich mit den
     Pfeiltasten verschieben. */
  const griffTaste = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const schritt = e.shiftKey ? 8 : 2
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        setzeBreite(breite + schritt)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        setzeBreite(breite - schritt)
      }
    },
    [breite, setzeBreite],
  )

  return (
    <div className="betrachter-flaeche" ref={flaeche}>
      {/* Die Seiten zeichnen wir selbst. Der eingebaute Betrachter des
          Browsers bringt eine dunkle Leiste, eine Seitenvorschau und einen
          fast schwarzen Grund mit — auf einer halben Bildschirmbreite frisst
          das Platz und steht neben einer hellen Oberflaeche wie ein
          Fremdkoerper.

          Beim Ziehen liegt eine Deckschicht darueber: sonst faengt das
          Canvas die Mausbewegung, und die Trennlinie bleibt an der Kante
          haengen. */}
      <div className="betrachter-blatt-huelle">
        <Blattbetrachter quelle={quelle} titel={titel} />
        {zieht && <div className="betrachter-deckschicht" />}
      </div>

      <div
        className={`betrachter-griff${zieht ? ' zieht' : ''}`}
        role="separator"
        aria-orientation="vertical"
        aria-label="Breite des Analysefelds"
        aria-valuenow={Math.round(breite)}
        aria-valuemin={BREITE_MIN}
        aria-valuemax={BREITE_MAX}
        tabIndex={0}
        onPointerDown={griffGedrueckt}
        onKeyDown={griffTaste}
        onDoubleClick={() => setzeBreite(BREITE_STANDARD)}
      />

      <div className="betrachter-feld" style={{ width: `${breite}%` }}>
        {children}
      </div>

      {daneben}
    </div>
  )
}
